import { getStartOfDay } from '@makemydeal/dr-common-utils';
import type { ValidationConfig, ValidationContext } from '../validationEngine/types';
import { addressValidationConfig } from './addressValidationConfig';
import { getRules, withoutRequiredRules } from './validationConfigRuleUtils';

// Inline/UI validations for InsuranceForm.
// Reuse the same address rules as addressValidationConfig (non-contract rules apply in default flow).
export const vehicleInsuranceInlineValidationConfig: ValidationConfig = {
    streetAddress: {
        rules: withoutRequiredRules(getRules(addressValidationConfig.streetAddress))
    },
    city: { rules: withoutRequiredRules(getRules(addressValidationConfig.city)) },
    zip: (addressValidationConfig as any).zip,
    bodilyInjuryPerPerson: {
        rules: [
            {
                type: 'CUSTOM',
                params: { evaluator: (v: unknown) => typeof v !== 'number' || v < 1000000 },
                messages: 'Value should be less than 1 million'
            }
        ]
    },
    bodilyInjuryPerAccident: {
        rules: [
            {
                type: 'CUSTOM',
                params: { evaluator: (v: unknown) => typeof v !== 'number' || v < 1000000 },
                messages: 'Value should be less than 1 million'
            }
        ]
    },
    insuranceAgencyName: {
        rules: [
            {
                type: 'MAX_LENGTH',
                params: { max: 40 },
                messages: 'Insurance Agency must be under 40 characters'
            }
        ]
    }
};

// Contract validations used for GC dynamic validations + contract validation runner.
export const vehicleInsuranceContractValidationConfig: ValidationConfig = {
    policyNumber: {
        rules: [
            {
                type: 'MAX_LENGTH',
                flow: 'contract',
                params: { max: 20 },
                messages: 'Insurance policy number must be less than 20 characters.'
            }
        ]
    },
    liabilityCoverage: {
        rules: [
            {
                type: 'EQUALS',
                flow: 'contract',
                params: { equals: 'true' },
                messages: 'Buyer liability insurance statement is required.',
                conditions: { applicableStates: ['CO'] }
            }
        ]
    },
    policyExpirationDate: {
        rules: [
            {
                type: 'CUSTOM',
                flow: 'contract',
                params: {
                    evaluator: (value: unknown) => getStartOfDay(new Date(value as string)) > getStartOfDay(new Date())
                },
                messages: 'Insurance policy expiration date must be a future date.'
            },
            {
                type: 'CUSTOM',
                flow: 'contract',
                params: {
                    evaluator: (value: unknown, ctx: ValidationContext) => {
                        const policyStartDate =
                            (ctx.values as any)?.vehicleInsurance?.policyStartDate ?? (ctx.values as any)?.policyStartDate;
                        if (!policyStartDate) return true;
                        return getStartOfDay(new Date(value as string)) > getStartOfDay(new Date(policyStartDate as string));
                    }
                },
                messages: 'Insurance policy expiration date must be after the policy start date.'
            }
        ]
    }
};

export const contractApiErroredVehicleInsuranceValidationConfig: ValidationConfig = {
    apiInsuranceCompany: {
        rules: [{ type: 'REQUIRED', flow: 'contract', messages: 'Insurance Provider is required' }]
    },
    apiAgentPhoneNumber: {
        rules: [{ type: 'REQUIRED', flow: 'contract', messages: 'Agent Phone Number is required' }]
    },
    apiPolicyNumber: {
        rules: [{ type: 'REQUIRED', flow: 'contract', messages: 'Policy Number is required' }]
    },
    apiPolicyStartDate: {
        rules: [{ type: 'REQUIRED', flow: 'contract', messages: 'Effective Date is required' }]
    },
    apiPolicyExpirationDate: {
        rules: [{ type: 'REQUIRED', flow: 'contract', messages: 'Expiration Date is required' }]
    }
};

// Convenience combined config (useful if a consumer wants both sets).
export const vehicleInsuranceValidationConfig: ValidationConfig = {
    ...vehicleInsuranceInlineValidationConfig,
    ...vehicleInsuranceContractValidationConfig
};
